import {Extension} from "@tiptap/core";
import {Plugin, PluginKey} from "@tiptap/pm/state";
import type {EditorView} from "@tiptap/pm/view";
import {Decoration, DecorationSet} from "@tiptap/pm/view";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {
    PRINTOUT_FOOTER_HEIGHT_VARIABLE,
    PRINTOUT_HEADER_HEIGHT_VARIABLE,
    PRINTOUT_REGION_ATTRIBUTE,
    PRINTOUT_REGION_BAND_ATTRIBUTE,
    PRINTOUT_REGION_BAND_CLASS,
    PRINTOUT_REGION_CLONE_CLASS,
    PRINTOUT_REGION_CONTENT_CLASS,
    PRINTOUT_REGION_DECORATION_BREAK_VARIABLE,
    PRINTOUT_REGION_PLACED_ATTRIBUTE,
} from "../blocks/headerFooter/printoutRegionOptions";
import type {PrintoutRegionKind} from "../blocks/headerFooter/printoutRegionOptions";
import {PRINTOUT_PAGE_BREAK_FILL_CLASS} from "../blocks/pageBreak/printoutPageBreakOptions";
import {getPrintoutPaginationOptions} from "../core/printoutPageGeometry";
import type {PrintoutPaginationOptions} from "../core/printoutPageGeometry";

export {A4_PRINTOUT_PAGE_SIZE} from "../core/printoutPageGeometry";
export type {PrintoutPageSize, PrintoutPaginationOptions} from "../core/printoutPageGeometry";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        printoutPagination: {
            updatePrintoutPagination: (options: PrintoutPaginationOptions) => ReturnType;
        };
    }
}

interface PrintoutPaginationStorage extends PrintoutPaginationOptions {
    appliedOptions: PrintoutPaginationOptions | null;
}

const PAGINATION_PLUGIN_KEY = new PluginKey("printoutPagination");
const HARD_BREAK_PLUGIN_KEY = new PluginKey("printoutPaginationHardBreaks");
const REPAGINATE_META_KEY = "printoutPaginationRepaginate";
const ROOT_CLASS = "crqlar-printout-paginated";
const STYLE_ELEMENT_ATTRIBUTE = "data-crqlar-printout-pagination-style";
const PAGINATION_WIDGET_SELECTOR = "[data-crqlar-printout-pagination]";
const PAGE_MARKER_CLASS = "crqlar-printout-page-marker";
const PAGE_GAP_CLASS = "crqlar-printout-page-gap";
const BR_DECORATION_CLASS = "crqlar-printout-br-decoration";

const DEFAULT_OPTIONS: PrintoutPaginationOptions = getPrintoutPaginationOptions();

const getContentHeight = (options: PrintoutPaginationOptions) => (
    Math.max(1, options.pageHeight - options.marginTop - options.marginBottom)
);

const optionsEqual = (a: PrintoutPaginationOptions | null, b: PrintoutPaginationOptions) => (
    !!a
    && a.pageHeight === b.pageHeight
    && a.pageWidth === b.pageWidth
    && a.marginTop === b.marginTop
    && a.marginBottom === b.marginBottom
    && a.marginLeft === b.marginLeft
    && a.marginRight === b.marginRight
    && a.pageGap === b.pageGap
    && a.pageGapBorderColor === b.pageGapBorderColor
    && a.pageBreakBackground === b.pageBreakBackground
);

const readOptions = (storage: PrintoutPaginationStorage): PrintoutPaginationOptions => ({
    pageHeight: storage.pageHeight,
    pageWidth: storage.pageWidth,
    marginTop: storage.marginTop,
    marginBottom: storage.marginBottom,
    marginLeft: storage.marginLeft,
    marginRight: storage.marginRight,
    pageGap: storage.pageGap,
    pageGapBorderColor: storage.pageGapBorderColor,
    pageBreakBackground: storage.pageBreakBackground,
});

const updateCssVariables = (targetNode: HTMLElement, options: PrintoutPaginationOptions) => {
    const cssVariables: Record<string, string> = {
        "--crqlar-printout-page-height": `${options.pageHeight}px`,
        "--crqlar-printout-page-width": `${options.pageWidth}px`,
        "--crqlar-printout-margin-top": `${options.marginTop}px`,
        "--crqlar-printout-margin-bottom": `${options.marginBottom}px`,
        "--crqlar-printout-margin-left": `${options.marginLeft}px`,
        "--crqlar-printout-margin-right": `${options.marginRight}px`,
        "--crqlar-printout-page-gap-border-color": options.pageGapBorderColor,
        "--crqlar-printout-content-height": `${getContentHeight(options)}px`,
    };

    Object.entries(cssVariables).forEach(([key, value]) => {
        targetNode.style.setProperty(key, value);
    });
};

const injectPaginationStylesheet = () => {
    if (document.head.querySelector(`style[${STYLE_ELEMENT_ATTRIBUTE}]`)) return;

    const style = document.createElement("style");

    style.setAttribute(STYLE_ELEMENT_ATTRIBUTE, "");
    style.textContent = `
      .${ROOT_CLASS} {
        border: 1px solid var(--crqlar-printout-page-gap-border-color);
        box-sizing: content-box;
        padding-top: var(--crqlar-printout-margin-top);
        padding-bottom: var(--crqlar-printout-margin-bottom);
        padding-left: var(--crqlar-printout-margin-left);
        padding-right: var(--crqlar-printout-margin-right);
        width: var(--crqlar-printout-page-width);
      }
      .${ROOT_CLASS} .${PAGE_GAP_CLASS} {
        border-top: 1px solid var(--crqlar-printout-page-gap-border-color);
        border-bottom: 1px solid var(--crqlar-printout-page-gap-border-color);
      }
      .${ROOT_CLASS} *:has(> br.ProseMirror-trailingBreak:only-child) {
        display: table;
        width: 100%;
      }
      .${ROOT_CLASS} .${BR_DECORATION_CLASS} {
        display: table;
        width: 100%;
      }
    `;
    document.head.appendChild(style);
};

// Marker offsets include root padding; min-height does not.
const refreshMinHeight = (editorDom: HTMLElement) => {
    const paginationElement = editorDom.querySelector<HTMLElement>(PAGINATION_WIDGET_SELECTOR);
    const lastPageMarker = paginationElement?.querySelector<HTMLElement>(
        `:scope > :last-child > .${PAGE_MARKER_CLASS}`,
    );

    if (!lastPageMarker) return;

    const paddingTop = Number.parseFloat(getComputedStyle(editorDom).paddingTop) || 0;

    editorDom.style.minHeight = `${lastPageMarker.offsetTop + lastPageMarker.offsetHeight - paddingTop}px`;
};

const getExistingPageCount = (view: EditorView) => {
    const paginationElement = view.dom.querySelector<HTMLElement>(PAGINATION_WIDGET_SELECTOR);

    return paginationElement ? paginationElement.children.length : 0;
};

const getTopLevelBlock = (element: HTMLElement, editorDom: HTMLElement) => {
    let current = element;

    while (current.parentElement && current.parentElement !== editorDom) {
        current = current.parentElement;
    }

    return current;
};

// Convert viewport measurements back to layout pixels under editor/preview zoom.
const getLayoutScale = (editorDom: HTMLElement) => {
    const scale = editorDom.getBoundingClientRect().height / editorDom.offsetHeight;

    return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

const getBottomWithin = (element: HTMLElement, editorDom: HTMLElement) => (
    element.getBoundingClientRect().bottom - editorDom.getBoundingClientRect().top
);

interface PrintoutRegionBands {
    header: number;
    footer: number;
}

const NO_REGION_BANDS: PrintoutRegionBands = {footer: 0, header: 0};

const REGION_SELECTOR = `[${PRINTOUT_REGION_ATTRIBUTE}]`;

const bandsEqual = (first: PrintoutRegionBands, second: PrintoutRegionBands) => (
    first.header === second.header && first.footer === second.footer
);

const getUsableContentHeight = (
    options: PrintoutPaginationOptions,
    bands: PrintoutRegionBands,
) => Math.max(1, getContentHeight(options) - bands.header - bands.footer);

const updateRegionCssVariables = (targetNode: HTMLElement, bands: PrintoutRegionBands) => {
    targetNode.style.setProperty(PRINTOUT_HEADER_HEIGHT_VARIABLE, `${bands.header}px`);
    targetNode.style.setProperty(PRINTOUT_FOOTER_HEIGHT_VARIABLE, `${bands.footer}px`);
    targetNode.style.setProperty(
        PRINTOUT_REGION_DECORATION_BREAK_VARIABLE,
        bands.header || bands.footer ? "clone" : "slice",
    );
};

interface PrintoutRegionElement {
    block: HTMLElement;
    content: HTMLElement;
    kind: PrintoutRegionKind;
}

const regionCloneSources = new WeakMap<HTMLElement, string>();

// Enabled regions always repeat and are out of flow before their bands are measured.
const measureRegionBands = (editorDom: HTMLElement) => {
    const bands: PrintoutRegionBands = {...NO_REGION_BANDS};
    const regions: PrintoutRegionElement[] = [];

    editorDom.querySelectorAll<HTMLElement>(REGION_SELECTOR).forEach((element) => {
        const kind = element.getAttribute(PRINTOUT_REGION_ATTRIBUTE);

        if (kind !== "header" && kind !== "footer") return;

        const block = getTopLevelBlock(element, editorDom);

        if (block.matches(PAGINATION_WIDGET_SELECTOR)) return;

        if (block.getAttribute(PRINTOUT_REGION_PLACED_ATTRIBUTE) !== kind) {
            block.setAttribute(PRINTOUT_REGION_PLACED_ATTRIBUTE, kind);
        }

        bands[kind] = Math.ceil(block.offsetHeight);
        regions.push({
            block,
            content: element.querySelector<HTMLElement>(`.${PRINTOUT_REGION_CONTENT_CLASS}`) ?? element,
            kind,
        });
    });

    return {bands, regions};
};

const syncRegionClone = (band: HTMLElement, content: HTMLElement) => {
    const source = content.innerHTML;

    if (regionCloneSources.get(band) === source) return;

    regionCloneSources.set(band, source);

    const clone = content.cloneNode(true) as HTMLElement;

    clone.classList.add(PRINTOUT_REGION_CLONE_CLASS);
    clone.removeAttribute("contenteditable");
    clone.removeAttribute("data-node-view-content");
    clone.querySelectorAll<HTMLElement>("[data-printout-block-action], .printout-block-dash, .printout-block-menu")
        .forEach((chrome) => chrome.remove());
    clone.querySelectorAll<HTMLElement>("[contenteditable], [id]").forEach((node) => {
        node.removeAttribute("contenteditable");
        node.removeAttribute("id");
    });

    band.innerHTML = "";
    band.appendChild(clone);
};

const applyRegionBands = (editorDom: HTMLElement, regions: PrintoutRegionElement[]) => {
    if (!regions.length) return;

    const paginationElement = editorDom.querySelector<HTMLElement>(PAGINATION_WIDGET_SELECTOR);

    if (!paginationElement) return;

    regions.forEach(({block, content, kind}) => {
        const bands = Array.from(paginationElement.querySelectorAll<HTMLElement>(
            `.${PRINTOUT_REGION_BAND_CLASS}[${PRINTOUT_REGION_BAND_ATTRIBUTE}="${kind}"]`,
        ));

        if (!bands.length) return;

        // Keep the editable header on page one and footer on the last page.
        const homeIndex = kind === "header" ? 0 : bands.length - 1;
        const top = `${bands[homeIndex].offsetTop}px`;

        if (block.style.top !== top) block.style.top = top;

        bands.forEach((band, index) => {
            if (index === homeIndex) {
                if (band.firstChild) band.innerHTML = "";
                regionCloneSources.delete(band);
                return;
            }

            syncRegionClone(band, content);
        });
    });
};

interface PrintoutPageFlowRegion {
    bottom: number;
    top: number;
}

// Read rendered boundaries directly; derived strides accumulate rounding drift.
const getPageFlowRegions = (editorDom: HTMLElement): PrintoutPageFlowRegion[] => {
    const paginationElement = editorDom.querySelector<HTMLElement>(PAGINATION_WIDGET_SELECTOR);

    if (!paginationElement) return [];

    const editorTop = editorDom.getBoundingClientRect().top;
    let previousPageBottom = 0;

    return Array.from(paginationElement.children).map((pageContainer) => {
        const marker = pageContainer.querySelector<HTMLElement>(`.${PAGE_MARKER_CLASS}`);
        const headerBand = pageContainer.querySelector<HTMLElement>(
            `.${PRINTOUT_REGION_BAND_CLASS}[${PRINTOUT_REGION_BAND_ATTRIBUTE}="header"]`,
        );
        const footerBand = pageContainer.querySelector<HTMLElement>(
            `.${PRINTOUT_REGION_BAND_CLASS}[${PRINTOUT_REGION_BAND_ATTRIBUTE}="footer"]`,
        );
        const markerTop = marker ? marker.getBoundingClientRect().top - editorTop : previousPageBottom;
        const region = {
            bottom: footerBand ? footerBand.getBoundingClientRect().top - editorTop : markerTop,
            top: headerBand ? headerBand.getBoundingClientRect().bottom - editorTop : previousPageBottom,
        };

        previousPageBottom = marker ? marker.getBoundingClientRect().bottom - editorTop : region.bottom;

        return region;
    });
};

const getPageBottoms = (editorDom: HTMLElement) => (
    getPageFlowRegions(editorDom).map((region) => region.bottom)
);

const getLastFlowElement = (editorDom: HTMLElement): HTMLElement | null => {
    let element = editorDom.lastElementChild;

    while (element) {
        if (
            !element.matches(PAGINATION_WIDGET_SELECTOR)
            && !element.hasAttribute(PRINTOUT_REGION_PLACED_ATTRIBUTE)
        ) {
            return element as HTMLElement;
        }

        element = element.previousElementSibling;
    }

    return null;
};

const CONVERGENCE_TOLERANCE_PX = 1;

interface PageCountConvergence {
    lastGrow: {overflow: number; pageCount: number} | null;
}

const calculatePageCount = (
    view: EditorView,
    options: PrintoutPaginationOptions,
    convergence: PageCountConvergence,
    bands: PrintoutRegionBands,
) => {
    const editorDom = view.dom;
    const contentHeight = getUsableContentHeight(options, bands);
    const paginationElement = editorDom.querySelector<HTMLElement>(PAGINATION_WIDGET_SELECTOR);
    const currentPageCount = getExistingPageCount(view);

    if (!paginationElement) {
        return Math.max(1, Math.ceil(editorDom.scrollHeight / contentHeight));
    }

    const lastContentElement = getLastFlowElement(editorDom);
    const flowRegions = getPageFlowRegions(editorDom);
    const lastFlowRegion = flowRegions[flowRegions.length - 1];

    if (!lastContentElement || !lastFlowRegion) return 1;

    const lastContentBottom = getBottomWithin(lastContentElement, editorDom);
    const overflow = lastContentBottom - lastFlowRegion.bottom;

    if (overflow > 0) {
        const {lastGrow} = convergence;

        // Oversized blocks settle instead of growing the page count forever.
        if (
            lastGrow
            && lastGrow.pageCount !== currentPageCount
            && overflow > lastGrow.overflow - CONVERGENCE_TOLERANCE_PX
        ) {
            return currentPageCount;
        }

        convergence.lastGrow = {overflow, pageCount: currentPageCount};

        return currentPageCount + Math.ceil(overflow / contentHeight);
    }

    convergence.lastGrow = null;

    // Count trailing empties by page starts; bottom markers create phantom pages.
    const emptyTrailingPages = flowRegions.filter((region) => region.top >= lastContentBottom).length;

    return currentPageCount - emptyTrailingPages;
};

const FILL_SELECTOR = `.${PRINTOUT_PAGE_BREAK_FILL_CLASS}`;
const FILL_LANDING_TOLERANCE_PX = 1;
const FILL_CORRECTION_PASSES = 3;

// Page-break fills are measured together because NodeView-local writes race each other.
const applyBlockFills = (view: EditorView) => {
    const editorDom = view.dom;
    const fills = Array.from(editorDom.querySelectorAll<HTMLElement>(FILL_SELECTOR));

    if (!fills.length) return;

    // Collapse all fills before measuring so stale heights cannot feed another fill.
    fills.forEach((element) => {
        element.style.height = "0px";
    });

    const pageBottoms = getPageBottoms(editorDom);

    if (!pageBottoms.length) return;

    const scale = getLayoutScale(editorDom);

    // Each fill depends only on the already-settled content above it.
    fills.forEach((element) => {
        const block = getTopLevelBlock(element, editorDom);

        // Nested fills measure against a moving ancestor and cannot converge.
        if (block.querySelector("[data-printout-block-type]") !== element.closest("[data-printout-block-type]")) return;

        const reference = block;
        const referenceBottom = getBottomWithin(reference, editorDom);
        // `>=` keeps an exact landing from consuming another page.
        const pageBottom = pageBottoms.find((bottom) => bottom >= referenceBottom);

        // Page count will grow; retry when the new marker exists.
        if (pageBottom === undefined) return;

        // Re-read after each write because floats make fill movement non-linear.
        let height = 0;
        let landedHeight = 0;
        let bottom = referenceBottom;

        for (let pass = 0; ; pass += 1) {
            const remaining = (pageBottom - bottom) / scale;

            if (Math.abs(remaining) <= FILL_LANDING_TOLERANCE_PX) {
                landedHeight = height;
                break;
            }

            // Retain the tallest undershoot; overshooting ejects a line to the next page.
            if (remaining > 0) landedHeight = height;

            const corrected = Math.max(0, height + remaining);

            if (pass === FILL_CORRECTION_PASSES || corrected === height) break;

            height = corrected;
            element.style.height = `${height}px`;
            bottom = getBottomWithin(reference, editorDom);
        }

        if (height !== landedHeight) element.style.height = `${landedHeight}px`;
    });
};

const createPaginationWidget = (
    options: PrintoutPaginationOptions,
    resolvePageCount: (view: EditorView) => number,
    bands: PrintoutRegionBands,
) => (
    Decoration.widget(0, (view: EditorView) => {
        const widget = document.createElement("div");

        widget.dataset.crqlarPrintoutPagination = "true";
        widget.setAttribute("aria-hidden", "true");
        widget.contentEditable = "false";

        const pageCount = Math.max(1, resolvePageCount(view));
        const contentHeight = getUsableContentHeight(options, bands);

        const createRegionBand = (kind: PrintoutRegionKind, height: number) => {
            const band = document.createElement("div");

            band.classList.add(PRINTOUT_REGION_BAND_CLASS);
            band.setAttribute(PRINTOUT_REGION_BAND_ATTRIBUTE, kind);
            band.style.position = "relative";
            band.style.float = "left";
            band.style.clear = "both";
            band.style.width = "100%";
            band.style.height = `${height}px`;

            return band;
        };

        for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
            const pageContainer = document.createElement("div");
            const pageSpace = document.createElement("div");
            const pageBreak = document.createElement("div");
            const bottomMarginSpace = document.createElement("div");
            const gap = document.createElement("div");
            const topMarginSpace = document.createElement("div");

            pageSpace.style.position = "relative";
            pageSpace.style.float = "left";
            pageSpace.style.clear = "both";
            pageSpace.style.marginTop = `${contentHeight}px`;

            pageBreak.classList.add(PAGE_MARKER_CLASS);
            pageBreak.style.position = "relative";
            pageBreak.style.float = "left";
            pageBreak.style.clear = "both";
            pageBreak.style.width = "calc(100% + var(--crqlar-printout-margin-left) + var(--crqlar-printout-margin-right))";
            pageBreak.style.marginLeft = "calc(-1 * var(--crqlar-printout-margin-left))";
            pageBreak.style.marginRight = "calc(-1 * var(--crqlar-printout-margin-right))";
            pageBreak.style.zIndex = "2";

            bottomMarginSpace.style.height = `${options.marginBottom}px`;
            bottomMarginSpace.style.backgroundColor = "transparent";

            gap.classList.add(PAGE_GAP_CLASS);
            gap.style.height = `${options.pageGap}px`;
            gap.style.position = "relative";
            gap.style.left = "-1px";
            gap.style.setProperty("width", "calc(100% + 2px)", "important");
            gap.style.backgroundColor = options.pageBreakBackground;
            gap.style.borderLeft = `1px solid ${options.pageBreakBackground}`;
            gap.style.borderRight = `1px solid ${options.pageBreakBackground}`;

            topMarginSpace.style.height = `${options.marginTop}px`;
            topMarginSpace.style.backgroundColor = "transparent";

            pageBreak.append(bottomMarginSpace, gap, topMarginSpace);

            // The last page ends at its marker; margins after it come from the editor's own padding.
            if (pageIndex === pageCount - 1) {
                pageBreak.style.visibility = "hidden";
                bottomMarginSpace.style.height = "0px";
                gap.style.height = "0px";
                topMarginSpace.style.height = "0px";
            }

            pageContainer.append(
                ...(bands.header ? [createRegionBand("header", bands.header)] : []),
                pageSpace,
                ...(bands.footer ? [createRegionBand("footer", bands.footer)] : []),
                pageBreak,
            );
            widget.appendChild(pageContainer);
        }

        return widget;
    }, {side: -1})
);

const buildHardBreakDecorations = (doc: ProseMirrorNode) => {
    const decorations: Decoration[] = [];

    doc.descendants((node, pos) => {
        if (node.type.name !== "hardBreak") return;

        decorations.push(Decoration.widget(pos + 1, () => {
            const element = document.createElement("span");

            element.classList.add(BR_DECORATION_CLASS);

            return element;
        }));
    });

    return DecorationSet.create(doc, decorations);
};

export const PrintoutPagination = Extension.create<PrintoutPaginationOptions, PrintoutPaginationStorage>({
    name: "printoutPagination",

    addOptions() {
        return {...DEFAULT_OPTIONS};
    },

    addStorage() {
        return {
            ...DEFAULT_OPTIONS,
            appliedOptions: null,
        };
    },

    onCreate() {
        Object.assign(this.storage, {...this.options, appliedOptions: null});

        const editorDom = this.editor.view.dom;

        editorDom.classList.add(ROOT_CLASS);
        injectPaginationStylesheet();
        updateCssVariables(editorDom, readOptions(this.storage));
    },

    addCommands() {
        return {
            updatePrintoutPagination: (options) => () => {
                Object.assign(this.storage, options);
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        const editor = this.editor;
        const storage = this.storage;

        const convergence: PageCountConvergence = {lastGrow: null};

        let appliedBands: PrintoutRegionBands = NO_REGION_BANDS;

        return [
            new Plugin({
                key: PAGINATION_PLUGIN_KEY,

                state: {
                    init: (_, state) => {
                        const options = readOptions(storage);

                        storage.appliedOptions = options;

                        return DecorationSet.create(state.doc, [
                            createPaginationWidget(
                                options,
                                (view) => calculatePageCount(view, options, convergence, appliedBands),
                                appliedBands,
                            ),
                        ]);
                    },
                    apply: (transaction, oldDecorations, _oldState, newState) => {
                        const options = readOptions(storage);
                        const optionsChanged = !optionsEqual(storage.appliedOptions, options);
                        const repaginating = !!transaction.getMeta(REPAGINATE_META_KEY);

                        if (transaction.docChanged || optionsChanged) convergence.lastGrow = null;

                        const existingPageCount = getExistingPageCount(editor.view);
                        const measuredPageCount = Math.max(
                            1,
                            calculatePageCount(editor.view, options, convergence, appliedBands),
                        );

                        // Pre-draw measurements may shrink, but growth waits for the post-layout sync.
                        const growthDeferred = !repaginating
                            && existingPageCount > 0
                            && measuredPageCount > existingPageCount;
                        const pageCount = growthDeferred ? existingPageCount : measuredPageCount;
                        const pageCountChanged = pageCount !== existingPageCount;

                        if (!optionsChanged && !pageCountChanged && !repaginating) {
                            return oldDecorations;
                        }

                        storage.appliedOptions = options;
                        updateCssVariables(editor.view.dom, options);

                        return DecorationSet.create(newState.doc, [
                            createPaginationWidget(options, () => pageCount, appliedBands),
                        ]);
                    },
                },

                props: {
                    decorations(state) {
                        return this.getState(state) as DecorationSet | undefined;
                    },
                },

                view: (editorView) => {
                    let scheduledFrame = 0;
                    let scheduledSyncFrame = 0;

                    const scheduleRepaginate = (view: EditorView) => {
                        if (scheduledFrame) return;

                        scheduledFrame = requestAnimationFrame(() => {
                            scheduledFrame = 0;

                            if (view.isDestroyed) return;

                            view.dispatch(view.state.tr.setMeta(REPAGINATE_META_KEY, true));
                        });
                    };

                    const syncPagination = (view: EditorView) => {
                        // Region bands define the usable height for every later measurement.
                        const {bands, regions} = measureRegionBands(view.dom);

                        if (!bandsEqual(bands, appliedBands)) {
                            appliedBands = bands;
                            updateRegionCssVariables(view.dom, bands);
                            scheduleRepaginate(view);
                            return;
                        }

                        applyRegionBands(view.dom, regions);

                        applyBlockFills(view);

                        const options = readOptions(storage);
                        const optionsChanged = !optionsEqual(storage.appliedOptions, options);

                        if (optionsChanged) convergence.lastGrow = null;

                        const pageCount = Math.max(1, calculatePageCount(view, options, convergence, bands));
                        const pageCountChanged = pageCount !== getExistingPageCount(view);

                        if (pageCountChanged || optionsChanged) {
                            scheduleRepaginate(view);
                            return;
                        }

                        refreshMinHeight(view.dom);
                    };

                    // Defer layout writes to avoid ResizeObserver loops after async NodeView/font/image changes.
                    const scheduleSync = () => {
                        if (scheduledSyncFrame) return;

                        scheduledSyncFrame = requestAnimationFrame(() => {
                            scheduledSyncFrame = 0;

                            if (editorView.isDestroyed) return;

                            syncPagination(editorView);
                        });
                    };

                    const resizeObserver = typeof ResizeObserver === "undefined"
                        ? null
                        : new ResizeObserver(scheduleSync);

                    resizeObserver?.observe(editorView.dom);

                    // Hidden tabs run neither rAF nor ResizeObserver; visibility must restart the pass.
                    const handleVisibilityChange = () => {
                        if (document.visibilityState !== "visible" || editorView.isDestroyed) return;

                        if (scheduledFrame) {
                            cancelAnimationFrame(scheduledFrame);
                            scheduledFrame = 0;
                        }

                        if (scheduledSyncFrame) {
                            cancelAnimationFrame(scheduledSyncFrame);
                            scheduledSyncFrame = 0;
                        }

                        syncPagination(editorView);
                    };

                    document.addEventListener("visibilitychange", handleVisibilityChange);

                    return {
                        update: syncPagination,
                        destroy: () => {
                            document.removeEventListener("visibilitychange", handleVisibilityChange);

                            if (scheduledFrame) cancelAnimationFrame(scheduledFrame);
                            if (scheduledSyncFrame) cancelAnimationFrame(scheduledSyncFrame);

                            resizeObserver?.disconnect();
                        },
                    };
                },
            }),

            new Plugin({
                key: HARD_BREAK_PLUGIN_KEY,

                state: {
                    init: (_, state) => buildHardBreakDecorations(state.doc),
                    apply: (transaction, oldDecorations) => (
                        transaction.docChanged ? buildHardBreakDecorations(transaction.doc) : oldDecorations
                    ),
                },

                props: {
                    decorations(state) {
                        return this.getState(state) as DecorationSet | undefined;
                    },
                },
            }),
        ];
    },
});
